import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { useI18n } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = ["Country", "State / Province", "County / District", "City / Town", "Village / Locality", "Just curious"];

export default function Waitlist() {
  const { tr } = useI18n();
  const [form, setForm] = useState({ email: "", name: "", territory_level: "", location_interest: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error(tr("Please enter your email."));
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, form);
      setDone(true);
      toast.success(tr("You're on the map. Welcome to WorldPulse."));
    } catch (err) {
      toast.error(err.response?.data?.detail || tr("Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="waitlist" data-testid="waitlist-section" className="py-24 lg:py-32" style={{ background: "linear-gradient(135deg, #F5F3FF 0%, #FAF5FF 55%, #EEF2FF 100%)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-start">
        <Reveal>
          <div>
            <span className="font-mono-wp text-xs uppercase tracking-[0.25em] text-[#7C3AED]">§ 08 — {tr("The Waitlist")}</span>
            <h2 className="font-atlas text-3xl sm:text-4xl lg:text-5xl mt-6 leading-[1.02] text-[#0F2557]">
              {tr("Claim your place")}<br />{tr("on the")} <em className="text-[#7C3AED]">{tr("map.")}</em>
            </h2>
            <p className="text-[#4A5568] mt-6 max-w-md leading-relaxed text-base">
              {tr("Founding partners, territory licensees and early contributors get first access as WorldPulse opens country by country.")}
            </p>
            <div className="font-mono-wp text-xs text-[#6D28D9] mt-10 space-y-2">
              <div>→ {tr("Territory licensing priority")}</div>
              <div>→ {tr("Early geo API access")}</div>
              <div>→ {tr("Founding contributor badge on SocialPulse")}</div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          {done ? (
            <div data-testid="waitlist-success-message" className="border border-[#86EFAC] rounded-3xl p-10 bg-[#F0FDF4]">
              <div className="font-mono-wp text-[0.65rem] uppercase tracking-[0.25em] text-[#15803D]">{tr("Registered")}</div>
              <div className="font-atlas text-3xl mt-4 text-[#0F2557]">{tr("Your coordinates are logged.")}</div>
              <p className="text-[#4A5568] text-sm mt-4 leading-relaxed">{tr("We'll reach out as your territory of interest comes online. The world is being indexed — one place at a time.")}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-[#E3D9F7] rounded-3xl shadow-lg shadow-purple-100 p-8 lg:p-10 space-y-7">
              <div>
                <label className="font-mono-wp text-[0.75rem] font-bold uppercase tracking-[0.2em] text-[#5B21B6] block mb-2">{tr("Name")}</label>
                <input
                  data-testid="waitlist-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={tr("Your full name")}
                  className="w-full bg-transparent border-b border-[#D8CCF2] py-3 text-[#0F2557] placeholder:text-[#8B7BB8] focus:outline-none focus:border-[#7C3AED] transition-colors font-atlas text-xl"
                />
              </div>
              <div>
                <label className="font-mono-wp text-[0.75rem] font-bold uppercase tracking-[0.2em] text-[#5B21B6] block mb-2">{tr("Email")} *</label>
                <input
                  data-testid="waitlist-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@worldpulse.pro"
                  className="w-full bg-transparent border-b border-[#D8CCF2] py-3 text-[#0F2557] placeholder:text-[#8B7BB8] focus:outline-none focus:border-[#7C3AED] transition-colors font-atlas text-xl"
                />
              </div>
              <div>
                <label className="font-mono-wp text-[0.75rem] font-bold uppercase tracking-[0.2em] text-[#5B21B6] block mb-3">{tr("Territory interest")}</label>
                <div data-testid="waitlist-territory-select" className="flex flex-wrap gap-2">
                  {LEVELS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      onClick={() => setForm({ ...form, territory_level: l })}
                      className={`font-mono-wp text-[0.75rem] font-bold uppercase tracking-[0.1em] px-3.5 py-2 border rounded-full transition-all duration-300 active:scale-[0.96] ${
                        form.territory_level === l
                          ? "bg-[#7C3AED] border-[#7C3AED] text-white"
                          : "border-[#C4B5E8] text-[#4C1D95] hover:border-[#7C3AED] hover:bg-[#F5F3FF]"
                      }`}
                    >
                      {tr(l)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="font-mono-wp text-[0.75rem] font-bold uppercase tracking-[0.2em] text-[#5B21B6] block mb-2">{tr("Which place matters to you?")}</label>
                <input
                  data-testid="waitlist-location-input"
                  value={form.location_interest}
                  onChange={(e) => setForm({ ...form, location_interest: e.target.value })}
                  placeholder={tr("e.g. Kerala, Oregon, Accra…")}
                  className="w-full bg-transparent border-b border-[#D8CCF2] py-3 text-[#0F2557] placeholder:text-[#8B7BB8] focus:outline-none focus:border-[#7C3AED] transition-colors font-atlas text-xl"
                />
              </div>
              <button
                data-testid="waitlist-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-mono-wp text-xs uppercase tracking-[0.25em] py-4 rounded-xl transition-colors duration-300 disabled:opacity-50 active:scale-[0.99]"
              >
                {loading ? tr("Plotting coordinates…") : `${tr("Join the waitlist")} →`}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
